import type {
  AddressAccPayload,
  AddressEntity,
  AddressFormFields,
  ClientInfoAddressView,
} from '~/types/address'
import type { IdentityView } from '~/types/identity'
import { useAddressesStore } from '~/stores/addresses'
import { useIdentity } from '~/composables/useIdentity'
import { floorsToClimbCount } from '~/utils/floor'

function firstPhone(phones: ClientInfoAddressView['phones']): string {
  const first = phones?.[0]
  if (!first) return ''
  if (typeof first === 'string') return first
  return first.phone ?? ''
}

function phoneList(phones: ClientInfoAddressView['phones']): string[] {
  if (!phones?.length) return []
  return phones
    .map((p) => (typeof p === 'string' ? p : p.phone ?? ''))
    .filter(Boolean)
}

function coerceFloorsToClimb(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function optionalTrim(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed || undefined
}

export function mapClientInfoAddress(item: ClientInfoAddressView): AddressEntity {
  const streetPart = item.street ?? ''
  const streetNumber = item.streetNumber ?? ''
  const label = item.nUsingTypeName ?? ([item.city, item.district].filter(Boolean).join(' - ') || 'Адрес за доставка')

  return {
    id: String(item.id),
    label,
    city: item.city ?? '',
    neighborhood: item.district ?? '',
    street: streetPart,
    streetNumber,
    block: item.block ?? '',
    entrance: item.entrance ?? '',
    floor: item.floor ?? '',
    apt: item.apartment ?? '',
    hasElevator: item.hasElevator ?? null,
    bringToTheDoor: item.bringToTheDoor ?? null,
    deliveryComment: item.additionalInfo ?? '',
    addressOrigin: item.addressOrigin ?? '',
    phone: firstPhone(item.phones),
    phones: phoneList(item.phones),
    floorsToClimb: coerceFloorsToClimb(item.floorsToClimb),
    deliveryStartTime: item.deliveryStartTime ?? null,
    deliveryEndTime: item.deliveryEndTime ?? null,
    lat: item.lat ?? null,
    lon: item.lon ?? null,
  }
}

export function streetForForm(address: Pick<AddressEntity, 'street' | 'streetNumber'>): string {
  return [address.street, address.streetNumber].filter(Boolean).join(' ')
}

export function toAccPayload(
  model: AddressFormFields,
  extra?: {
    id?: number
    lat?: number | null
    lon?: number | null
    bringToTheDoor?: boolean | null
    floor1IsGroundLevel?: boolean
    clearGps?: boolean
  },
): AddressAccPayload {
  const trimmedStreet = model.street.trim()
  const payload: AddressAccPayload = {
    city: model.city.trim(),
    district: model.neighborhood.trim(),
    street: trimmedStreet,
    floorsToClimb: String(floorsToClimbCount(model.floor, model.hasElevator, {
      bringToTheDoor: extra?.bringToTheDoor,
      floor1IsGroundLevel: extra?.floor1IsGroundLevel,
    })),
    hasElevator: model.hasElevator,
    bringToTheDoor: extra?.bringToTheDoor ?? null,
  }

  const block = optionalTrim(model.block)
  if (block) payload.block = block
  const entrance = optionalTrim(model.entrance)
  if (entrance) payload.entrance = entrance
  const floor = optionalTrim(model.floor)
  if (floor) payload.floor = floor
  const apartment = optionalTrim(model.apt)
  if (apartment) payload.apartment = apartment
  const additionalInfo = optionalTrim(model.deliveryComment)
  if (additionalInfo) payload.additionalInfo = additionalInfo
  if (model.phones) {
    payload.phones = model.phones.map(p => p.trim()).filter(Boolean)
  }
  else {
    const phone = optionalTrim(model.phone)
    if (phone) payload.phone = phone
  }
  if (extra?.id != null) payload.id = extra.id
  if (extra?.clearGps) payload.clearGps = true
  else {
    if (extra?.lat != null) payload.lat = extra.lat
    if (extra?.lon != null) payload.lon = extra.lon
  }

  return payload
}

export const useAddresses = () => {
  const store = useAddressesStore()
  const api = useApi()
  const { fetchIdentity } = useIdentity()

  const getAddressById = (id: string) => store.getById(id)

  const upsertAddress = (address: AddressEntity) => store.upsert(address)

  /** Both clients' addresses; also returns the whole identity, so callers need not fetch it twice. */
  async function fetchAddresses(userId: number): Promise<IdentityView> {
    const data = await fetchIdentity(userId)
    store.setAddresses((data.addresses ?? []).map(mapClientInfoAddress))
    return data
  }

  async function addAddress(body: AddressAccPayload): Promise<void> {
    await api('/security/delivery-address-add', { method: 'POST', body })
  }

  async function updateAddress(body: AddressAccPayload): Promise<void> {
    await api('/security/delivery-address-update', { method: 'POST', body })
  }

  return {
    addresses: computed(() => store.addresses),
    getAddressById,
    upsertAddress,
    fetchAddresses,
    addAddress,
    updateAddress,
  }
}
